import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

import { HomeNav } from "@/components/home-nav";
import { SiteFooter } from "@/components/site-footer";
import { getJourney, journeyProjects } from "@/data/journeys";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/think")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://www.luizajesusdoprado.com/think" },
      { title: "Think — Science, Articles & Research | Luiza Jesus do Prado" },
      {
        name: "description",
        content:
          "Scientific and artistic research, articles and social projects by Luiza Jesus do Prado.",
      },
      {
        property: "og:title",
        content: "Think — Science, Articles & Research | Luiza Jesus do Prado",
      },
      { property: "og:description", content: "Science, articles, research and social projects." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Think,
});

function Think() {
  const { lang } = useI18n();
  const journey = getJourney("think");
  const items = journeyProjects("think");
  const [hover, setHover] = useState(0);
  const inset = items[hover]?.cover ?? items[0]?.cover;

  return (
    <div className="min-h-screen bg-red text-ink">
      <HomeNav tone="light" />

      <main id="main-content" className="relative px-6 pb-28 pt-28 md:px-12 md:pt-32">
        <div className="flex items-start justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-ink/70">
          <span>{String(items.length).padStart(3, "0")}</span>
          <span>{journey.label[lang]}</span>
          <span>/100</span>
        </div>

        <div className="relative mx-auto mt-16 max-w-[1100px]">
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-ink/60">
            [{journey.description[lang]}]
          </p>

          <ul className="mt-10 text-center">
            {items.map((p, i) => (
              <li key={p.slug}>
                <Link
                  to="/work/$slug"
                  params={{ slug: p.slug }}
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  className={`block font-sans text-[clamp(1.7rem,6.4vw,5rem)] font-medium uppercase leading-[0.98] tracking-[-0.02em] transition-opacity duration-300 ${
                    hover === i ? "text-ink" : "text-ink/70 hover:text-ink"
                  }`}
                >
                  {p.title}
                </Link>
              </li>
            ))}
          </ul>

          {/* centered photo inset */}
          {inset ? (
            <div className="pointer-events-none absolute left-1/2 top-1/2 w-[46%] max-w-[380px] -translate-x-1/2 -translate-y-1/2 border border-ink/30">
              <img
                src={inset}
                alt=""
                aria-hidden
                className="aspect-[4/3] w-full object-cover contrast-125"
              />
            </div>
          ) : null}
        </div>

        <div className="mt-24 grid gap-6 font-mono text-[10px] uppercase leading-[1.6] tracking-[0.18em] text-ink/75 md:grid-cols-2">
          <p className="max-w-[34ch]">
            Research is treated as material: articles, protocols and experiments that translate
            science into image, sound and gesture.
          </p>
          <p className="max-w-[34ch] md:justify-self-end">
            Neuroscience, psychology, semiotics and social practice — a transdisciplinary index of
            ongoing investigations.
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
